/** Content → blog comments: moderate, reply, bulk actions. /sites/:id/comments */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Avatar, Button, Card, Empty, Input, Modal, Popconfirm, Segmented, Space, Table, Tag, Typography, message } from 'antd';
import { useTranslation } from '../../../shared/i18n';
import { fmtDate, listOf, toastErr, totalOf, useSite } from './common';

const { Text } = Typography;
const COLORS = { pending: 'gold', approved: 'green', spam: 'red' };
const LABEL = { pending: 'Pending', approved: 'Approved', spam: 'Spam' };

const Comments = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [sp] = useSearchParams();
    const [f, setF] = useState({ status: sp.get('status') ?? 'pending', q: '', page: 1, per_page: 25, post_id: sp.get('post_id') || '' });
    const [data, setData] = useState({ items: [], total: 0 });
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [sel, setSel] = useState([]);
    const [edit, setEdit] = useState(null); // {mode: reply|edit, c, body}
    const load = useCallback(() => {
        setLoading(true);
        api.get('/comments/counts').then((d) => setCounts(d || {})).catch(() => {});
        return api.get('/comments', Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')))
            .then((d) => setData({ items: listOf(d), total: totalOf(d) })).catch(toastErr).finally(() => setLoading(false));
    }, [api, f]);
    useEffect(() => { load(); setSel([]); }, [load]);
    const act = async (fn) => { try { await fn(); await load(); } catch (e) { toastErr(e); } };
    const bulk = (action, ids = sel) => act(async () => { await api.post('/comments/bulk', { ids, action }); setSel([]); });
    const submit = () => act(async () => {
        if (!edit.body.trim()) return;
        if (edit.mode === 'reply') await api.post(`/comments/${edit.c.id}/reply`, { body: edit.body });
        else await api.put(`/comments/${edit.c.id}`, { body: edit.body });
        setEdit(null);
        message.success(t('Saved'));
    });
    const n = (k) => (counts[k] !== undefined ? ` (${counts[k]})` : '');
    return (
        <Card title={t('Comments')}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Segmented value={f.status} onChange={(v) => setF((x) => ({ ...x, status: v, page: 1 }))} options={[
                    { value: 'pending', label: t('Pending') + n('pending') }, { value: 'approved', label: t('Approved') + n('approved') },
                    { value: 'spam', label: t('Spam') + n('spam') }, { value: '', label: t('All') },
                ]} />
                <Input.Search allowClear placeholder={t('Search')} style={{ width: 220 }} onSearch={(v) => setF((x) => ({ ...x, q: v, page: 1 }))} />
                {sel.length > 0 && (
                    <Space size={4}>
                        <Button size="small" onClick={() => bulk('approve')}>{t('Approve')}</Button>
                        <Button size="small" onClick={() => bulk('spam')}>{t('Spam')}</Button>
                        <Popconfirm title={t('Delete selected?')} okButtonProps={{ danger: true }} onConfirm={() => bulk('delete')}><Button size="small" danger>{t('Delete')}</Button></Popconfirm>
                    </Space>
                )}
            </Space>
            <Table rowKey="id" loading={loading} dataSource={data.items} scroll={{ x: 720 }}
                rowSelection={{ selectedRowKeys: sel, onChange: setSel }}
                pagination={{ current: f.page, pageSize: f.per_page, total: data.total, hideOnSinglePage: true, onChange: (page, per) => setF((x) => ({ ...x, page, per_page: per })) }}
                locale={{ emptyText: <Empty description={t('No comments')} /> }}
                columns={[
                    { title: t('Author'), key: 'a', width: 200, render: (_, c) => (
                        <Space><Avatar size="small" src={c.avatar}>{(c.author_name || '?')[0]}</Avatar><div><div>{c.author_name}{c.is_owner && <Tag style={{ marginLeft: 4 }}>{t('Owner')}</Tag>}</div><Text type="secondary" style={{ fontSize: 12 }}>{c.email}</Text></div></Space>
                    ) },
                    { title: t('Comment'), key: 'b', render: (_, c) => (
                        <div>
                            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{c.body}</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>{c.post_title} · {fmtDate(c.created_at)}</Text>
                            <div style={{ marginTop: 4 }}>
                                <Space size={2} wrap>
                                    {c.status !== 'approved' && <Button size="small" type="link" onClick={() => bulk('approve', [c.id])}>{t('Approve')}</Button>}
                                    {c.status === 'approved' && <Button size="small" type="link" onClick={() => bulk('pending', [c.id])}>{t('Unapprove')}</Button>}
                                    <Button size="small" type="link" onClick={() => setEdit({ mode: 'reply', c, body: '' })}>{t('Reply')}</Button>
                                    <Button size="small" type="link" onClick={() => setEdit({ mode: 'edit', c, body: c.body })}>{t('Edit')}</Button>
                                    {c.status !== 'spam' && <Button size="small" type="link" onClick={() => bulk('spam', [c.id])}>{t('Spam')}</Button>}
                                    <Popconfirm title={t('Delete this comment?')} okButtonProps={{ danger: true }} onConfirm={() => act(() => api.del(`/comments/${c.id}`))}><Button size="small" type="link" danger>{t('Delete')}</Button></Popconfirm>
                                </Space>
                            </div>
                        </div>
                    ) },
                    { title: t('Status'), dataIndex: 'status', width: 100, render: (v) => <Tag color={COLORS[v]}>{t(LABEL[v] || v)}</Tag> },
                ]} />
            <Modal open={!!edit} title={edit?.mode === 'reply' ? t('Reply') : t('Edit')} onCancel={() => setEdit(null)} onOk={submit} destroyOnHidden>
                <Input.TextArea autoFocus autoSize={{ minRows: 4 }} value={edit?.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
            </Modal>
        </Card>
    );
};

export default Comments;
